/**
 * Learn.tsx - Main learning page that hosts the program explorer and lessons.
 * Simplified for Dashboard integration.
 */

import React, { useState } from 'react';
import { 
  Trophy, 
  Flame, 
  Star,
  Target,
  TrendingUp 
} from 'lucide-react';
import ProgramExplorer from './ProgramExplorer';
import type { Lesson } from '../services/learningApi';

const Learn: React.FC = () => {
  const [lessonsCompleted] = useState(0);
  const [totalXP] = useState(0);
  const [currentStreak] = useState(0);
  const [recentLessons, setRecentLessons] = useState<Lesson[]>([]);

  const handleLessonGenerated = (lesson: Lesson) => {
    console.log('Lesson generated:', lesson);
    setRecentLessons((prev) => [lesson, ...prev.slice(0, 4)]);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Système d'Apprentissage</h1>
          <p className="text-gray-500">Programme CAFOP - Génération progressive des leçons</p>
        </div>
        
        {/* Stats bar */}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 text-orange-500 bg-orange-50 px-3 py-1.5 rounded-lg">
            <Flame className="w-5 h-5" />
            <span className="font-semibold">{currentStreak}</span>
            <span className="text-sm text-gray-500">jours</span>
          </div>
          <div className="flex items-center gap-2 text-yellow-500 bg-yellow-50 px-3 py-1.5 rounded-lg">
            <Star className="w-5 h-5" />
            <span className="font-semibold">{totalXP}</span>
            <span className="text-sm text-gray-500">XP</span>
          </div>
          <div className="flex items-center gap-2 text-green-500 bg-green-50 px-3 py-1.5 rounded-lg">
            <Trophy className="w-5 h-5" />
            <span className="font-semibold">{lessonsCompleted}</span>
            <span className="text-sm text-gray-500">leçons</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left sidebar - Quick stats */}
        <div className="space-y-4">
          {/* Daily goal */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-gray-900">Objectif du jour</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Leçons</span>
                <span className="text-gray-900">0 / 3</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full">
                <div className="h-full bg-blue-600 rounded-full" style={{ width: '0%' }} />
              </div>
            </div>
          </div>

          {/* Progress overview */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span className="font-medium text-gray-900">Progression</span>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">📚 Français</span>
                  <span className="text-gray-900">0%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: '0%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">🔢 Mathématiques</span>
                  <span className="text-gray-900">0%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: '0%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">🌍 Culture Générale</span>
                  <span className="text-gray-900">0%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div className="h-full bg-purple-500 rounded-full" style={{ width: '0%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">🧠 Psychotechniques</span>
                  <span className="text-gray-900">0%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div className="h-full bg-orange-500 rounded-full" style={{ width: '0%' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Recent lessons */}
          {recentLessons.length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <h3 className="font-medium text-gray-900 mb-3">Leçons récentes</h3>
              <div className="space-y-2">
                {recentLessons.map((lesson, index) => (
                  <div
                    key={index}
                    className="p-2 bg-gray-50 rounded text-sm"
                  >
                    <div className="font-medium text-gray-800 truncate">
                      {lesson.title}
                    </div>
                    <div className="text-xs text-gray-500">
                      {lesson.subtitle}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main content area - Program Explorer */}
        <div className="lg:col-span-3">
          <ProgramExplorer onLessonGenerated={handleLessonGenerated} />
        </div>
      </div>
    </div>
  );
};

export default Learn;
